import { GovIcon, GovMessage } from '@gov-design-system-ce/react';
import { useTranslationWithNamespace } from '@gov-nx/core/service';
import type { PageCode } from '@gov-nx/module/page';
import {
	DokladyType,
	DokumentyReklamaceContextProvider,
	useDokladyContext,
} from '@gov-nx/module/page';
import {
	Button,
	ButtonGroupLayout,
	ContentLayout,
	CopyButton,
	DataTable,
	Dialog,
	LayoutGap,
} from '@gov-nx/ui/web';
import { isInPastOrNearFuture, toDateReadable } from '@gov-nx/utils/common';
import { MarkdownRender } from '../../Markdown/MarkdownRender';
import { DocumentComplainSubmitButton } from './DocumentComplaintSubmitButton';
import { DocumentComplaintView } from './DocumentComplaintView';
import { IdentityDocumentSkeleton } from './IdentityDocumentSkeleton';

export interface IdentifyDocumentViewProps {
	type: DokladyType;
	code: PageCode;
}

const getFormId = (documentId: number | undefined) =>
	`document-complain-form-${documentId}`;

export const IdentityDocumentView = ({
	type,
	code,
}: IdentifyDocumentViewProps) => {
	const { getLocalizeCurried } = useTranslationWithNamespace();
	const tsn = getLocalizeCurried(code);
	const {
		getDocumentByType,
		isDocumentLoadingByType,
		controls,
		setControls,
		canChangeLicence,
		zadostNovyRidicskyPrukazUrl,
	} = useDokladyContext();

	if (isDocumentLoadingByType(type)) {
		return <IdentityDocumentSkeleton />;
	}

	return (
		<LayoutGap gap="m">
			{getDocumentByType(type).map((document) => {
				const showWarning = isInPastOrNearFuture(document.platnostDo);

				return (
					<ContentLayout key={document.cisloDokladu}>
						<LayoutGap gap="m">
							<DataTable responsive>
								<tbody>
									{document.druhDokladu === DokladyType.P ? (
										<tr>
											<td className="sm:w-1/2">{tsn('type')}</td>
											<td className="sm:w-1/2">{document.druhDokladu}</td>
										</tr>
									) : null}
									<tr>
										<td className="sm:w-1/2">{tsn('number')}</td>
										<td className="sm:w-1/2">
											<strong>{document.cisloDokladu}</strong>
											<CopyButton
												label={
													tsn('akce.kopirovat-cislo-dokladu') +
													' ' +
													document.cisloDokladu
												}
												value={document.cisloDokladu as string}
											/>
										</td>
									</tr>
									{document.platnostOd ? (
										<tr>
											<td>{tsn('platnostOd')}</td>
											<td>
												<strong>{toDateReadable(document.platnostOd)}</strong>
											</td>
										</tr>
									) : null}
									{document.platnostDo ? (
										<tr>
											<td>{tsn('platnostDo')}</td>
											<td className="flex items-center gap-2">
												<strong>{toDateReadable(document.platnostDo)}</strong>
												{showWarning && (
													<GovIcon
														type="components"
														name="exclamation-triangle-fill"
														slot="icon"
														color="warning"
													/>
												)}
											</td>
										</tr>
									) : null}
								</tbody>
							</DataTable>
							{canChangeLicence &&
							type === DokladyType.RP &&
							zadostNovyRidicskyPrukazUrl ? (
								<GovMessage
									color="warning"
									type="subtle"
									icon={
										<GovIcon
											type="components"
											name="exclamation-triangle-fill"
										/>
									}>
									<MarkdownRender>
										{tsn('ridicsky-prukaz.akce.prodlouzeni', {
											url: zadostNovyRidicskyPrukazUrl,
										})}
									</MarkdownRender>
								</GovMessage>
							) : null}
							{type !== DokladyType.RP && (
								<ButtonGroupLayout>
									<Button
										onClick={() =>
											setControls({ displayComplaintModal: document })
										}
										className="mt-6"
										size="m"
										color="primary"
										type="outlined"
										isExpandedMobile
										aria-label={tsn('akce.podnet-k-oprave-udaju')}>
										{tsn('akce.podnet-k-oprave-udaju')}
									</Button>
									{controls.displayComplaintModal ? (
										<DokumentyReklamaceContextProvider
											code={code}
											document={controls.displayComplaintModal}
											onComplaintSubmit={() =>
												setControls({ displayComplaintModal: undefined })
											}>
											<Dialog
												isOpen={!!controls.displayComplaintModal}
												onClose={() =>
													setControls({ displayComplaintModal: undefined })
												}
												title={<h3>{tsn('formular.podnet-k-oprave-udaju')}</h3>}
												accessibleCloseLabel={tsn('akce.wcag.zavrit-formular')}
												footer={
													<DocumentComplainSubmitButton
														code={code}
														formId={getFormId(document.dokladId)}
													/>
												}>
												<DocumentComplaintView
													formId={getFormId(document.dokladId)}
													code={code}
												/>
											</Dialog>
										</DokumentyReklamaceContextProvider>
									) : null}
								</ButtonGroupLayout>
							)}
						</LayoutGap>
					</ContentLayout>
				);
			})}
		</LayoutGap>
	);
};
